import React from 'react';

import { MergeFieldRegistryEntry } from '~forms/types';

import MergeFieldToken, { parseInlineTokens } from '../MergeFieldToken';
import { sanitizeRichTextHtml } from './sanitizeRichText';

// Mirrors the sanitiser allow-list. Anything outside it can't appear post-sanitise, but we guard the
// element-creation step too so a tag never reaches React.createElement by name unchecked.
const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li']);

// Split a text run on {{merge:<token>}} sentinels, rendering the tokens as the shared blue chip so
// rich-text merge fields look identical to inline merge fields used elsewhere (labels, prefills).
function renderTextWithTokens(text: string, registry: MergeFieldRegistryEntry[], keyPrefix: string): React.ReactNode {
  return parseInlineTokens(text).map((seg, i) =>
    seg.type === 'token' ? (
      <MergeFieldToken key={`${keyPrefix}-t${i}`} token={seg.value} registry={registry} />
    ) : (
      <React.Fragment key={`${keyPrefix}-s${i}`}>{seg.value}</React.Fragment>
    ),
  );
}

function nodeToReact(node: Node, registry: MergeFieldRegistryEntry[], key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return <React.Fragment key={key}>{renderTextWithTokens(node.textContent ?? '', registry, key)}</React.Fragment>;
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const children = childrenToReact(el, registry, key);
    // Defensive: a non-allow-listed element (shouldn't survive sanitise) renders as its children only.
    if (!ALLOWED_TAGS.has(tag)) {
      return <React.Fragment key={key}>{children}</React.Fragment>;
    }
    if (tag === 'br') {
      return React.createElement('br', { key });
    }
    return React.createElement(tag, { key }, children);
  }
  return null;
}

function childrenToReact(parent: Node, registry: MergeFieldRegistryEntry[], keyPrefix: string): React.ReactNode[] {
  return Array.from(parent.childNodes).map((child, i) => nodeToReact(child, registry, `${keyPrefix}-${i}`));
}

/**
 * Renders sanitised rich-text HTML as React, turning inline {{merge:<token>}} sentinels into the shared
 * MergeFieldToken chip. Used for the read-only filler view and the read-only canvas preview.
 *
 * Safer than dangerouslySetInnerHTML: only allow-listed tags are recreated and all text becomes escaped
 * React children. The DOMParser only ever sees already-sanitised markup.
 */
export function renderRichText(html: string, registry: MergeFieldRegistryEntry[] = []): React.ReactNode {
  const clean = sanitizeRichTextHtml(html);
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  return childrenToReact(doc.body, registry, 'rt');
}
